import type { SocialPostDraftCard, SocialPostPreviewCardMetadata } from '@wix/ai-scheduling-assistant-common-lib/entities';
import {
  contentGoOutAt,
  contentHeldAt,
  contentKindOf,
  contentPublishAt,
  contentPublishedOf,
  contentStateOf,
  draftCardOf,
  readPostPlan,
  type ContentKind,
  type ContentState,
  type EmailPreviewCard,
  type PostPlanFormat,
} from '@wix/ai-scheduling-assistant-common-lib/utils';
import type { DraftKind } from './draft-preview';
import type { TaskContentFields } from './task-content-fields';

export type TaskContentSource = TaskContentFields & {
  status?: string;
  postAt?: string | Date;
  socialPostPreviewCard?: SocialPostPreviewCardMetadata;
  contentSocialCard?: SocialPostPreviewCardMetadata;
  displayOrigin?: string;
  blockedByDecisions?: { decisionId: string; resolvedChoice?: string }[];
};

export interface TaskContentView {
  kind: ContentKind;
  state: Exclude<ContentState, 'dropped' | 'failed'>;
  network?: string;
  format?: PostPlanFormat;
  concept?: string;
  publishAt?: string;
  socialCard?: SocialPostPreviewCardMetadata;
  publishedUrl?: string;
  publishedAt?: string;
  scheduledAt?: string;
  draft?: { draftId: string; kind: DraftKind; title: string; subject: string; post?: SocialPostDraftCard };
  canGenerate: boolean;
}

const NETWORK_NAMES: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  x: 'X',
  twitter: 'X',
  pinterest: 'Pinterest',
  youtube: 'YouTube',
  threads: 'Threads',
  google_business: 'Google Business Profile',
};

export type ScheduledPostAction = 'edit' | 'drop';

export function networkName(network: string): string {
  return NETWORK_NAMES[network] ?? network.replace(/_/g, ' ').replace(/^\w/, (first) => first.toUpperCase());
}

export function taskContentView(
  task: TaskContentSource,
  now: number,
  enabled: { email?: boolean; blog?: boolean; publishTime?: boolean },
  started = false,
): TaskContentView | undefined {
  if (enabled.publishTime !== true) return undefined;
  const anyCard = task.contentSocialCard ?? task.socialPostPreviewCard;
  const card = anyCard && typeof anyCard.decisionId === 'string' ? anyCard : undefined;
  const socialDraft = card ? undefined : task.socialPostDraftCard;
  const display: Record<string, unknown> = {
    ...(card ? { socialPostPreviewCard: card } : socialDraft ? { socialPostPreviewCard: socialDraft } : {}),
    ...(task.postPlan ? { postPlan: task.postPlan } : {}),
    ...(task.delivery ? { delivery: task.delivery } : {}),
    ...(task.savedActionId ? { savedActionId: task.savedActionId } : {}),
    ...(task.publishedPost ? { publishedPost: task.publishedPost } : {}),
    ...(task.displayOrigin ? { origin: task.displayOrigin } : {}),
    ...(task.emailPreviewCard ? { emailPreviewCard: task.emailPreviewCard } : {}),
    ...(task.blogPostPreviewCard ? { blogPostPreviewCard: task.blogPostPreviewCard } : {}),
    ...(task.sentEmail ? { sentEmail: task.sentEmail } : {}),
    ...(task.publishedBlogPost ? { publishedBlogPost: task.publishedBlogPost } : {}),
    ...(task.prepareNowAt ? { prepareNowAt: task.prepareNowAt } : {}),
    ...(task.replacedBy ? { replacedBy: task.replacedBy } : {}),
    ...(!task.delivery?.publishAt && postAtIso(task.postAt) ? { marketingPlanPost: { postAt: postAtIso(task.postAt) } } : {}),
  };
  const kind = contentKindOf(display);
  const derived = contentStateOf({ status: task.status, blockedByDecisions: task.blockedByDecisions, display }, now);
  if (!kind || !derived || derived === 'dropped' || derived === 'failed') return undefined;
  const state = started && !task.prepareNowAt && derived === 'idea' ? 'drafting' : derived;
  const plan = readPostPlan(display);
  const publishAt = contentGoOutAt(display);
  const planned = Date.parse(contentPublishAt(display) ?? '');
  const toggled = kind === 'social' || (kind === 'email' ? enabled.email === true : enabled.blog === true);
  const socialDraftCard = kind === 'social' ? socialDraft : undefined;
  const draftCard = draftCardOf(display, kind);
  const published = contentPublishedOf(display);
  const scheduledAt = publishAt ?? contentHeldAt(display) ?? card?.scheduledAt;
  return {
    kind,
    state,
    ...(kind === 'social' && plan?.network ? { network: plan.network } : {}),
    ...(kind === 'social' && plan?.format ? { format: plan.format } : {}),
    ...(plan?.concept ? { concept: plan.concept } : {}),
    ...(publishAt ? { publishAt } : {}),
    ...(kind === 'social' && card ? { socialCard: card } : {}),
    ...(state === 'done' && published?.url ? { publishedUrl: published.url } : {}),
    ...(state === 'done' && published?.at ? { publishedAt: published.at } : {}),
    ...(kind === 'social' && state === 'scheduled' && scheduledAt ? { scheduledAt } : {}),
    ...(socialDraftCard
      ? { draft: { draftId: socialDraftCard.draftId, kind: 'SOCIAL_POST' as const, title: '', subject: '', post: socialDraftCard } }
      : draftCard
      ? {
          draft: {
            draftId: draftCard.draftId,
            kind: kind === 'email' ? ('EMAIL' as const) : ('BLOG_POST' as const),
            title: draftCard.title,
            subject: kind === 'email' ? (draftCard as EmailPreviewCard).subject : '',
          },
        }
      : {}),
    canGenerate: state === 'idea' && Number.isFinite(planned) && planned > now && toggled,
  };
}

function postAtIso(value: string | Date | undefined): string | undefined {
  const at = value ? new Date(value) : undefined;
  return at && !Number.isNaN(at.getTime()) ? at.toISOString() : undefined;
}
